import {FolderView} from "../files/files";
import {CompanyBase} from "@models/company";
import {BuilderVESummary, DeveloperVESummary} from "../ve/ve_model";
import {BidderSov, SovLine} from "../project_conf/bidConfig_model";
import {BaseScopeModel, FullScopeModel} from "../scope/ScopeModel";


/**
 * Can bidders see the amount of other bidders?
 * */
export enum AmountViewMode {
    // the amounts are visible
    Visible = 'visible',

    // They do not see an amount, but they see a ranking
    Ranked = 'ranked',

    // NO - they can't see the amounts
    Hidden = 'hidden',

    LowMediumHigh = 'lowmidhigh',

    Diagram = 'diagram'
}


export const AmountViewExplanation = {
    'hidden': "Bidders will not see competitor amounts",
    'ranked': "Bidders will not see competitor amounts, but will know where they rank in total",
    'visible': "Bidders will see competitor bid amounts",
    'lowmidhigh': "",
    'diagram': "Bidders will see a diagram showing where their bid falls relative to others",
}

/**
 * Can bidders see the names of other bidders?
 * */
export enum NameViewMode {

    // they can see the names, and this corresponds to either a ranking or a list of amounts
    Visible = 'visible',

    // They can see the names, but as an independant list
    Detached = 'detached',

    // NO - they can't see the names
    Hidden = 'hidden'
}

export const NamedViewExplanation = {
    'hidden': "Bidders will not know who they are competing against",
    'detached': "Bidders will see who they are competing against, but will not know who has what price",
    'visible': "Bidders will see who they are competing against",

}


export enum BidCreationType {
    bidder= 'bidder',
    qlink='qlink'
}


export interface BidderViewMode {
    // Define the possible statuses here
    names: NameViewMode;
    amounts: AmountViewMode;
}



export interface BidConfig {
    bid_view_mode: BidderViewMode,
}


export interface BidViewItem {
    name: string;
    amount: number | string
    yours: boolean;
}


export interface LinkedBidViewItem extends BidViewItem {
    url: string;
}


export interface BidPartial {
    /**
     * user editable parts of the BID
     * */
    title: string;
    bid_amount: number;
    project_id: string;
    id: string;
}

/**
 * The data that bidders have access to for market feedback.
 * Since there are 9 modes of displaying this feedback, we get an object that supports all 9 modes.
 * */
export interface DiagramPoint {
    position: number;
    yours: boolean;
    name: string | null;
}

export interface BiddingView {
    latest: BidBaseModel | BidPartial;
    names: string[] | null;
    items: BidViewItem[] | null;
    ranking: number | null;
    num_bidders: number;
    mode: BidderViewMode;
    diagram?: DiagramPoint[] | null;
    scope?: BaseScopeModel | null;
}


export interface DeveloperBiddingView {
    items: LinkedBidViewItem[];
}

export enum BidStatus {
    // Define the possible statuses here
    Draft = 'draft',
    Submitted = 'submitted',
    Accepted = 'accepted'
}

export enum BidResponseStatus {
    // Define the possible statuses here
    rejected = 'rejected',
    in_review = 'in_review',
    validate = 'validate'
}


export interface BidSummary {
    bid_amount: number;
    created_at: Date;
    status: BidStatus;
    submitted_at?: Date;
    _id?: string;
    id?: string;
    project_id: string;
    creation_type?: BidCreationType;
    created_by?: string;
    link_id: string;
    company_name?: string;
}

export interface BidSummaryModel {
    /**
     * Used by developer for
     * */
    user?: string;
    company: CompanyBase;
    latest_bid?: BidSummary;
    ve_summary: BuilderVESummary;
    num_bids?: number;
    project_id: string;
    link_id?: string;

}

export interface BidEditableModel {
    /**
     * user editable parts of Bid Data
     * */
    title: string;
    bid_amount: number;
    description: string;
    inclusions: string;
    exclusions: string;
    status: BidStatus;
}


export function blank_bid(): BidEditableModel {
    return {
        title: "",
        bid_amount: 0,
        description: "",
        inclusions: "",
        exclusions: "",
        status: BidStatus.Draft,
    };
}


export interface BidSubmitModel{
    status: BidStatus;
}

// where the bid lives in the project's bid tab workbook. see backend/leveling
export interface BidLevelingLink {
    sheet_name: string;
    sheet_range?: string | null;
    // bidder column header - sync follows it if the column moves
    title_cell?: string | null;
    title_value?: string | null;
    // excel online url of the workbook
    web_url?: string | null;
    base_bid_cell: string;
    total_cell: string;
    base_bid_amount?: number | null;
    // last value read from total_cell - only becomes developer.leveled_amount when matched
    leveled_amount?: number | null;
    // base bid in the workbook == bid amount. false = workbook not updated for this revision
    matched: boolean;
    // title / sheet not found on last sync - re-run setup
    lost?: boolean;
    last_synced_at?: string | null;
}

// where leveled_amount came from. 'leveled' = set by a human - bid tab sync never overwrites it
export type LevelingStatus = "default" | "leveled" | "linked" | "ai_leveled";

// leveling plug. see backend/leveling/plug_model.py
export type PlugSource = "manual" | "ai";
// add / osv count toward the leveled amount, alt / info do not
export type PlugKind = "add" | "alt" | "osv" | "info" | "included" | "excluded";

export interface Plug {
    id: string;
    // the SOV cell id. null = base bid
    cell_id: number | null;
    // added to the line. negative takes out
    amount: number;
    note: string;
    kind: PlugKind;
    source: PlugSource;
    updated_at: string;
    updated_by?: string | null;
    // carried from the bidder's previous bid onto their revision - until the developer checks it
    stale?: boolean;
}

// returned by every plug write
export interface PlugsView {
    plugs: Plug[];
    leveled_amount: number;
}

export interface BidDeveloperData {
    leveled_amount?: number | null;
    leveling_status?: LevelingStatus;
    link?: BidLevelingLink | null;
    plugs?: Plug[];
    // developer is carrying this bid in the buyout
    carried?: boolean | null;
}

export interface BidBaseModel extends BidEditableModel {

    // indexers
    id: string;
    project_id: string;
    company_id: string;
    company_name: string;
    created_by: string;
    number: number;

    // business data - Commented in for reference
    // title: string;
    // bid_amount: number;
    // description: string;
    // inclusions: string;
    // exclusions: string;
    // status: BidStatus;

    is_latest: boolean;
    created_at: Date;
    submitted_at?: Date;
    modified_at: Date;
    status: BidStatus;
    response_status: BidResponseStatus;
    // developer only - backend strips this for builders
    developer?: BidDeveloperData | null;

    // tool.period
    period: string;

    // tool.root
    root:FolderView;

    // the SOV, rendered for the viewer's role (get_bid only). null - the bid has no SOV
    sov_lines?: SovLine[] | null;
    // the bidder's SOV values - what the bid stores, and what save / submit / copy send
    sov?: BidderSov | null;
    scope_id?: string | null;

    feedback?: BiddingView | null;

}

export interface ProjectCard {
    name: string;
    company_name: string;
}

export interface BidExtendedModel extends BidBaseModel {
    project: ProjectCard | null
    scope: FullScopeModel | null
}
